import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import NeuralNexusBackground from '../components/shared/NeuralNexusBackground';
import { DESIGN_SYSTEM } from '../theme/designSystem';

const TIER_LABELS = {
  foundations: { label: 'Foundations', color: '#4f8ef7' },
  operator:    { label: 'Full Operator', color: '#7c3aed' },
  certified:   { label: 'Certified Operator', color: '#f59e0b' },
};

export default function Dashboard() {
  const { currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, 'users', currentUser.uid));
        if (snap.exists()) setUserData(snap.data());
      } catch (err) {
        console.error('Failed to load user data:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [currentUser.uid]);

  if (loading) return <LoadingSpinner message="Loading..." />;

  const tier = isAdmin ? 'certified' : (userData?.tier || null);
  const tierInfo = tier ? TIER_LABELS[tier] : null;

  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
      background: DESIGN_SYSTEM.backgrounds.course,
      fontFamily: "'Inter', -apple-system, sans-serif",
      paddingTop: `${DESIGN_SYSTEM.layout.globalNavHeight}px`,
      boxSizing: 'border-box',
    }}>
      <NeuralNexusBackground />
      {/* Tier badge bar */}
      <div style={{
        position: 'relative',
        zIndex: 1,
        padding: '16px 40px',
        borderBottom: '1px solid rgba(153,179,214,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'transparent',
      }}>
        <div style={{ fontSize: '14px', fontWeight: 700, color: '#f0f4ff', letterSpacing: '0.08em' }}>
          THE OPERATOR METHOD
        </div>
        {tierInfo && (
          <span style={{
            padding: '4px 12px',
            backgroundColor: 'rgba(79,142,247,0.1)',
            border: `1px solid ${tierInfo.color}`,
            borderRadius: '20px',
            fontSize: '12px',
            color: tierInfo.color,
            fontWeight: 600,
          }}>
            {tierInfo.label}
          </span>
        )}
      </div>

      <div style={{ position: 'relative', zIndex: 1, maxWidth: '680px', margin: '0 auto', padding: '80px 24px' }}>

        {/* No tier — prompt to enroll */}
        {!tier && (
          <div style={{
            padding: '40px 36px',
            backgroundColor: '#0d1220',
            border: '1px solid #1e2a3a',
            borderRadius: '16px',
            textAlign: 'center',
            marginBottom: '32px',
          }}>
            <h3 style={{ color: '#f0f4ff', fontSize: '22px', fontWeight: 700, margin: '0 0 12px 0' }}>
              Enroll to start your training
            </h3>
            <p style={{ color: '#6b7a99', fontSize: '15px', margin: '0 0 28px 0', lineHeight: 1.7 }}>
              Choose your tier to get access to your AI instructor.
            </p>
            <button
              onClick={() => navigate('/pricing')}
              style={{ padding: '14px 36px', backgroundColor: '#4f8ef7', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 700, cursor: 'pointer' }}
            >
              See pricing &rarr;
            </button>
          </div>
        )}

        {/* Primary training CTA */}
        {tier && (
          <div style={{
            padding: '48px 40px',
            background: '#0a0e1a',
            border: '1px solid rgba(153, 179, 214, 0.24)',
            borderRadius: '14px',
            textAlign: 'center',
            boxShadow: '0 34px 78px rgba(1, 6, 20, 0.58), 0 12px 24px rgba(1, 6, 20, 0.42), inset 0 1px 0 rgba(233, 243, 255, 0.08)',
          }}>
            <div style={{ fontSize: '11px', letterSpacing: '0.22em', textTransform: 'uppercase', color: '#4f8ef7', fontWeight: 700, marginBottom: '16px' }}>
              AI Instructor &mdash; Live
            </div>
            <h2 style={{ color: '#f0f4ff', margin: '0 0 12px 0', fontSize: '28px', fontWeight: 800, lineHeight: 1.2 }}>
              Your instructor is ready.
            </h2>
            <p style={{ color: '#6b7a99', margin: '0 0 36px 0', fontSize: '16px', lineHeight: 1.7, maxWidth: '420px', marginLeft: 'auto', marginRight: 'auto' }}>
              Adaptive, direct coaching, one-on-one. Every session builds on where you left off.
            </p>
            <button
              onClick={() => navigate('/v2-course')}
              style={{ padding: '16px 48px', backgroundColor: '#4f8ef7', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '17px', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.02em' }}
            >
              Enter Training &rarr;
            </button>
          </div>
        )}

        {/* Day One orientation link */}
        {tier && (
          <div style={{ textAlign: 'center', marginTop: '14px' }}>
            <button
              onClick={() => navigate('/day-one')}
              style={{ background: 'none', border: 'none', color: '#4a6d8c', fontSize: '13px', cursor: 'pointer', fontWeight: 500, fontFamily: 'inherit' }}
            >
              Revisit Day One Guide &rarr;
            </button>
          </div>
        )}

        {/* Upgrade nudge for foundations tier */}
        {tier === 'foundations' && (
          <div style={{
            marginTop: '24px',
            padding: '20px 28px',
            backgroundColor: '#0d1220',
            border: '1px solid #1e2a3a',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '20px',
            flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ fontSize: '13px', color: '#f0f4ff', fontWeight: 600 }}>Ready to go further?</div>
              <div style={{ fontSize: '13px', color: '#4a5568', marginTop: '3px' }}>Upgrade to Full Operator or Certified to unlock the complete curriculum.</div>
            </div>
            <button
              onClick={() => navigate('/pricing')}
              style={{ flexShrink: 0, padding: '9px 22px', backgroundColor: 'transparent', border: '1px solid #4f8ef7', borderRadius: '8px', color: '#4f8ef7', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
            >
              Upgrade &rarr;
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
